"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import ProjectsSlider from "@/components/sections/projects-slider";
import Link from "next/link";

interface HomePageProjectsClientProps {
  lang: string;
  projects: any[];
  homepageContent: any;
}

export default function HomePageProjectsClient({
  lang,
  projects,
  homepageContent,
}: HomePageProjectsClientProps) {
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection for optimized animations
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Animation variants - exact match from original
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: isMobile ? 30 : 60, // Reduced movement on mobile
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isMobile ? 0.6 : 0.8, // Faster animation on mobile
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <motion.section
      className="py-16 sm:py-20 lg:py-[100px] relative w-full project-section-bg"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={sectionVariants}
    >
      {/* Section Header */}
      <div className="text-center mb-[48px] px-4">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-[20px] sm:w-[30px] h-[1px] bg-neutral-700"></div>
          <span className="text-dark-200 text-[14px] sm:text-[16px] font-medium leading-[16px] capitalize">
            {homepageContent?.projects?.title || "Projects"}
          </span>
          <div className="w-[20px] sm:w-[30px] h-[1px] bg-neutral-700"></div>
        </div>

        <Link
          href="/en/projects"
          className="text-dark-200 text-[32px] sm:text-[40px] lg:text-[48px] font-medium leading-[38px] sm:leading-[48px] lg:leading-[58px] tracking-[-1.44px] text-center"
        >
          {homepageContent?.projects?.subtitle || "Explore Our Projects"}
        </Link>
      </div>

      {/* Projects Slider - Full Width */}
      <div className="relative w-full">
        <ProjectsSlider projects={projects || []} />
      </div>
    </motion.section>
  );
}
